import {
	BusyIndicator,
	DateString,
	Pagination,
	TableView
} from "client/Components";
import {
	convertSortDirectionToDto,
	DataSourceSortableState,
	DataSourceSortDirection
} from "client/Data/sorting";
import {
	AlertManager,
	createRemoteDataSource,
	RemoteDataSourceState,
	useAlerts,
	useSortHandler,
	useUserTimezone
} from "client/Hooks";
import { isNil } from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	ReactElement,
	RefObject,
	useEffect,
	useRef,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { SettingsPage } from "../SettingsPage";
import {
	AuditLogItemModel,
	AuditLogOrderModel,
	AuditLogSortProperty,
	list
} from "client/Api/AuditLogs/auditLogs";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { TimeZone } from "client/DateAndTime/timezones";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export const Page = (): ReactElement => {
	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
	const [sorting, setSorting] = useState<DataSourceSortableState<AuditLogItemModel>>({
		propertyName: "eventTime",
		direction: DataSourceSortDirection.Descending
	});

	const tableRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const alertManager: AlertManager = useAlerts();
	useActiveSettingsMenuItem(SettingsPageKind.AuditLogging);
	const userTimezone: TimeZone = useUserTimezone();

	const {
		currentItems,
		getLoadingState,
		pagination
	} = useAuditLogDataSource({
		sorting: sorting,
		pagination: {
			currentPageNumber,
			amountPerPage: 50
		}
	});

	const { sortingFor } = useSortHandler(
		sorting,
		setSorting,
		true
	);

	const loadingState: RemoteDataSourceState = getLoadingState();

	useEffect(
		() => {
			if (loadingState === "error") {
				alertManager.showError(localize("_UnableToRetrieveItems"));
			}
		},
		[
			alertManager,
			loadingState
		]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(sorting);

			setCurrentPageNumber(0);
		},
		[sorting]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(currentPageNumber, sorting);

			tableRef.current
				?.scrollTo(0, 0);
		},
		[currentPageNumber, sorting]
	);

	return (
		<SettingsPage
			title={localize("_AuditLogging")}
		>
			<SettingsPage.Section height="stretch">
				{loadingState === "loading" && (
					<BusyIndicator />
				)}
				<TableView
					items={currentItems}
					itemKey="id"
					columns={[
						{
							title: localize("_Timestamp"),
							sortable: sortingFor("eventTime"),
							trackingId: "timestamp"
						},
						{
							title: localize("_UserUPN"),
							trackingId: "user-upn",
							sortable: sortingFor("userUpn"),
						},
						{
							title: localize("_Action"),
							trackingId: "action",
							maxWidth: Number.MAX_SAFE_INTEGER,
						}
					]}
					noResultConfig={{
						icon: "gear",
						title: localize("_NoLoggingsYet"),
						message: localize("_ThereAreNoLoggingsYet")
					}}
					trackingId="audit-log-table"
					ref={tableRef}
					height="stretch"
				>
					{(x) => (
						<TableView.Row
							key={x.id}
							trackingId={x.id.toString()}
						>
							<TableView.Cell>
								<DateString
									value={x.eventTime}
									format="DMYT"
									timezone={userTimezone}
								/>
							</TableView.Cell>
							<TableView.Cell>{x.userUpn}</TableView.Cell>
							<TableView.Cell>{x.actionDescription}</TableView.Cell>
						</TableView.Row>
					)}
				</TableView>
				<Pagination
					{...pagination}
					onPageNumberChanged={setCurrentPageNumber}
					trackingId="audit-log-pagination"
				/>
			</SettingsPage.Section>
			<AiAssistantFabSpacing />
		</SettingsPage>
	);
};
Page.displayName = "Page";

const useAuditLogDataSource = createRemoteDataSource<AuditLogItemModel>()
	.setConfiguration({
		hasPagination: true
	})
	.getHook({
		performRequest: (options): Promise<PageResult<AuditLogItemModel>> => {
			const {
				amountPerPage,
				pageNumber,
				sorting,
				signal
			} = options;

			if (isNil(sorting) || sorting.direction === DataSourceSortDirection.None) {
				throw new Error("Invalid sorting direction");
			}

			let sortProperty: AuditLogSortProperty;
			switch (sorting.propertyName)
			{
				case "eventTime":
					sortProperty = AuditLogSortProperty.EventTime;
					break;
				case "userUpn":
					sortProperty = AuditLogSortProperty.Upn;
					break;
				default:
					throw new Error(`'${sorting.propertyName}' is not supported`);
			}

			const order: AuditLogOrderModel = {
				property: sortProperty,
				direction: convertSortDirectionToDto(sorting.direction)
			};

			return list(
				order,
				amountPerPage,
				pageNumber * amountPerPage,
				signal
			).catch(
				(e: unknown) => {
					if (pageNumber === 0) {
						handlePageDataLoadingError(e);
					} else {
						throw e;
					}
				}
			);
		}
	});

export const auditLogsRouting = {
	page: "/AuditLogs"
};